import { ReactNode, useMemo, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AuthGuard } from "@/components/admin/AuthGuard";
import { adminAPI } from "@/lib/admin-api";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  Calendar,
  Building2,
  FileText,
  Settings,
  LogOut,
  Upload,
  MapPin,
  Image,
  Menu,
  ExternalLink,
  LayoutTemplate,
  Inbox,
} from "lucide-react";

interface AdminLayoutProps {
  children: ReactNode;
}

type NavItem = {
  icon: typeof LayoutDashboard;
  label: string;
  href: string;
};

type NavGroup = {
  title: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    title: "Overview",
    items: [{ icon: LayoutDashboard, label: "Dashboard", href: "/admin" }],
  },
  {
    title: "Program",
    items: [
      { icon: FileText, label: "Events", href: "/admin/event" },
      { icon: Users, label: "Speakers", href: "/admin/speakers" },
      { icon: Calendar, label: "Agenda", href: "/admin/agenda" },
      { icon: Building2, label: "Sponsors", href: "/admin/sponsors" },
    ],
  },
  {
    title: "Enquiries",
    items: [
      { icon: Inbox, label: "Registration", href: "/admin/registrations" },
    ],
  },
  {
    title: "Website",
    items: [
      { icon: LayoutTemplate, label: "Homepage", href: "/admin/homepage" },
      { icon: MapPin, label: "Venue", href: "/admin/venue" },
      { icon: Image, label: "Gallery", href: "/admin/gallery" },
      { icon: Settings, label: "About", href: "/admin/about" },
      { icon: Upload, label: "Media", href: "/admin/media" },
    ],
  },
];

function isActivePath(href: string, pathname: string) {
  if (href === "/admin") {
    return pathname === "/admin" || pathname === "/admin/";
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

function formatRole(role?: string) {
  if (!role) return "Editor";
  return role
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getInitials(name?: string, username?: string) {
  const source = name?.trim() || username?.trim() || "A";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function SidebarNav({
  pathname,
  onNavigate,
}: {
  pathname: string;
  onNavigate?: () => void;
}) {
  return (
    <nav className="flex-1 space-y-6 overflow-y-auto px-3 py-4">
      {NAV_GROUPS.map((group) => (
        <div key={group.title}>
          <p className="mb-2 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {group.title}
          </p>
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const active = isActivePath(item.href, pathname);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  activeOptions={item.href === "/admin" ? { exact: true } : undefined}
                  onClick={onNavigate}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                    active
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground/80 hover:bg-secondary hover:text-foreground",
                  )}
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SidebarAccount() {
  const user = adminAPI.getCurrentUser() as {
    full_name?: string;
    username?: string;
    role?: string;
  } | null;

  const handleLogout = async () => {
    try {
      await adminAPI.logout();
    } finally {
      window.location.href = "/admin/login";
    }
  };

  return (
    <div className="border-t p-3">
      <div className="mb-3 flex items-center gap-3 px-1">
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
          {getInitials(user?.full_name, user?.username)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {user?.full_name || user?.username || "Admin"}
          </p>
          <p className="truncate text-xs text-muted-foreground">{formatRole(user?.role)}</p>
        </div>
      </div>
      <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
        <LogOut className="mr-2 h-4 w-4" />
        Sign out
      </Button>
    </div>
  );
}

function SidebarBrand() {
  return (
    <div className="border-b px-5 py-5">
      <p className="font-serif text-lg text-primary">Events UASA</p>
      <p className="text-xs text-muted-foreground">Content workspace</p>
    </div>
  );
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const pageTitle = useMemo(() => {
    for (const group of NAV_GROUPS) {
      const match = group.items.find((item) => isActivePath(item.href, pathname));
      if (match) return match.label;
    }
    return "Dashboard";
  }, [pathname]);

  return (
    <AuthGuard>
      <div className="min-h-screen bg-muted/40">
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r bg-card md:flex">
          <SidebarBrand />
          <SidebarNav pathname={pathname} />
          <SidebarAccount />
        </aside>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="flex w-72 flex-col p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Admin navigation</SheetTitle>
            </SheetHeader>
            <SidebarBrand />
            <SidebarNav pathname={pathname} onNavigate={() => setMobileOpen(false)} />
            <SidebarAccount />
          </SheetContent>
        </Sheet>

        <div className="md:pl-64">
          <header className="sticky top-0 z-20 border-b bg-background/90 backdrop-blur-md">
            <div className="flex h-14 items-center justify-between gap-3 px-4 sm:px-6">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <div>
                  <p className="text-sm font-medium text-foreground">{pageTitle}</p>
                  <p className="hidden text-xs text-muted-foreground sm:block">
                    Manage conference content
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild>
                <a href="/" target="_blank" rel="noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  View site
                </a>
              </Button>
            </div>
          </header>

          <main className="px-4 py-6 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-6xl">{children}</div>
          </main>
        </div>
      </div>
    </AuthGuard>
  );
}
